// What the docked panel on Home and in the Playground shows: how the Console
// works, the request as the call your code makes, and one answer from this
// session. See Design > Console.

import { useId, useState } from "preact/hooks";
import { Button, CodeBlock, Icon, PillGroup, StatusBadge } from "../../../ui/src/index.ts";
import type { PillOption } from "../../../ui/src/index.ts";
import { answersOf, shortHub } from "../domain/answer.ts";
import { curlFor } from "../domain/curl.ts";
import { usd } from "../domain/money.ts";
import { isEmptyRequest, parseRequestText } from "../domain/request.ts";
import type { AskRequest } from "../domain/types.ts";
import { startFromText } from "./actions.ts";
import { href } from "./router.ts";
import { markSeen } from "./setup.ts";
import { useStore, type LedgerEntry } from "./store.ts";

export type PanelTab = "answer" | "lesson" | "how" | "api";

/** The panel's tabs and its close button, for DockPanel's head. */
export function PanelHead({ tab, tabs, onTab, onClose }: { tab: PanelTab; tabs: PillOption<PanelTab>[]; onTab: (t: PanelTab) => void; onClose: () => void }) {
  return <>
    <PillGroup label="Panel" value={tab} onChange={onTab} options={tabs} />
    <Button variant="ghost" size="sm" icon aria-label="Close panel" onClick={onClose}><Icon name="close" size={16} /></Button>
  </>;
}

/** Copies text, saying how it went for a moment. */
export function useCopy(): [string | null, (text: string, then?: () => void) => void] {
  const [said, setSaid] = useState<string | null>(null);
  const copy = (text: string, then?: () => void) => {
    const done = (r: string) => { setSaid(r); setTimeout(() => setSaid(null), 1800); };
    if (!navigator.clipboard) { done("Couldn't copy"); return; }
    navigator.clipboard.writeText(text).then(() => { done("Copied"); then?.(); }, () => done("Couldn't copy"));
  };
  return [said, copy];
}

export function HowItWorks() {
  const id = useId();
  return <>
    <h2>How the Console works</h2>
    <svg class="cs-howflow" viewBox="0 0 416 248" role="img" aria-labelledby={`${id}-title ${id}-desc`}>
      <title id={`${id}-title`}>How the Console works</title>
      <desc id={`${id}-desc`}>Your question and bid reach three hubs. One answers with its sources and price; two decline at no cost.</desc>
      <g class="edge" data-from="question" data-to="answers"><path d="M116 124 C132 124 124 48 140 48" class="cs-howflow__link" /></g>
      <g class="edge" data-from="question" data-to="decline-1"><path d="M116 124 H140" class="cs-howflow__link" /></g>
      <g class="edge" data-from="question" data-to="decline-2"><path d="M116 124 C132 124 124 200 140 200" class="cs-howflow__link" /></g>
      <g class="edge" data-from="answers" data-to="answer">
        <path d="M276 48 C288 48 284 104 284 116 Q284 124 288 124 H294" class="cs-howflow__hit-link" />
        <path d="M289 119 L294 124 L289 129" class="cs-howflow__hit-link" />
      </g>
      <g class="node" data-node="question" data-role="default">
        <rect x="12" y="68" width="104" height="120" class="cs-howflow__sheet" />
        <rect x="8" y="64" width="108" height="120" class="cs-howflow__box" />
        <rect x="20" y="76" width="28" height="28" class="cs-howflow__glyph" />
        <Icon name="questions" size={20} x={24} y={80} class="cs-howflow__icon" />
        <text x="20" y="128" class="cs-howflow__title">question</text>
        <text x="20" y="154" class="cs-howflow__muted">+ your bid</text>
      </g>
      <g class="node" data-node="answers" data-role="output">
        <rect x="140" y="16" width="136" height="64" class="cs-howflow__hit" />
        <Icon name="hub" size={20} x={152} y={38} class="cs-howflow__hit-icon" />
        <text x="180" y="55" class="cs-howflow__hit-text cs-howflow__title">answers</text>
      </g>
      <g class="node" data-node="decline-1" data-role="agent">
        <rect x="140" y="92" width="136" height="64" class="cs-howflow__miss" />
        <Icon name="hub" size={16} x={152} y={116} class="cs-howflow__quiet-icon" />
        <text x="176" y="119" class="cs-howflow__muted">declines,</text>
        <text x="176" y="143" class="cs-howflow__muted">free</text>
      </g>
      <g class="node" data-node="decline-2" data-role="agent">
        <rect x="140" y="168" width="136" height="64" class="cs-howflow__miss" />
        <Icon name="hub" size={16} x={152} y={192} class="cs-howflow__quiet-icon" />
        <text x="176" y="195" class="cs-howflow__muted">declines,</text>
        <text x="176" y="219" class="cs-howflow__muted">free</text>
      </g>
      <g class="node" data-node="answer" data-role="default">
        <rect x="304" y="60" width="104" height="136" class="cs-howflow__sheet" />
        <rect x="300" y="56" width="108" height="136" class="cs-howflow__box" />
        <rect x="312" y="68" width="28" height="28" class="cs-howflow__glyph" />
        <Icon name="check" size={20} x={316} y={72} class="cs-howflow__icon" />
        <text x="312" y="124" class="cs-howflow__title">answer</text>
        <text x="312" y="151" class="cs-howflow__muted">+ sources</text>
        <text x="312" y="178" class="cs-howflow__muted">+ price</text>
      </g>
    </svg>
    <ol class="cs-howlist">
      <li><strong>You ask a question, with a bid.</strong> The bid is the most you'll pay for one answer.</li>
      <li><strong>Hubs that know the subject answer.</strong> A hub that can't answer says so, at no cost.</li>
      <li><strong>You get the answer and its sources.</strong> You pay the hub's price, never more than your bid.</li>
      <li><strong>Wrong answer? Reject it.</strong> You're refunded at once.</li>
    </ol>
    <p class="cs-panel-note">Most requests come from code. The Playground is where you try them first and see exactly what your code will get.</p>
  </>;
}

/** The request the Playground holds, or the first example, as the call your code makes. */
export function UseTheApi() {
  const apiBase = useStore((s) => s.config.apiBase);
  const account = useStore((s) => s.user?.account);
  const text = useStore((s) => s.requestText);
  const example = useStore((s) => s.content.useCases[0]);
  const [said, copy] = useCopy();
  const held = parseRequestText(text).request;
  const request: AskRequest | null = held && !isEmptyRequest(held) ? held
    : example ? { questions: [{ id: "q1", text: example.text, maxCostPerAnswer: example.maxCostPerAnswer }] } : null;
  return <>
    <h2>Call it from your code</h2>
    <p class="cs-panel-note">Every Playground request is one call to the Consumer API, sent with your WarmHub token. {held && !isEmptyRequest(held) ? "This is the request in the Playground." : "This is an example request."}</p>
    {request ? <>
      <CodeBlock label="Request" code={curlFor(apiBase, request)} />
      <div class="cs-row-actions">
        <Button size="sm" onClick={() => copy(curlFor(apiBase, request), () => markSeen(account, "copied"))}>{said ?? "Copy request"}</Button>
        <Button size="sm" href="https://docs.warmhub.ai" target="_blank" rel="noreferrer">API reference</Button>
      </div>
      <span class="ui-sr-only" aria-live="polite">{said ?? ""}</span>
    </> : <p class="cs-panel-note" role="status">Loading an example request…</p>}
  </>;
}

const when = (at: number) => new Date(at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

/** One answer from this session's ledger: what it cost, and how to see it again. */
export function AnswerDetail({ entry }: { entry: LedgerEntry }) {
  const apiBase = useStore((s) => s.config.apiBase);
  const account = useStore((s) => s.user?.account);
  const refunded = useStore((s) => s.refunded.includes(entry.answerId));
  // Still on screen in the Playground when it came from the newest response.
  const showing = useStore((s) => s.response?.kind === "ok" && s.response.value.questions.some((q) => answersOf(q).some((a) => a.answerId === entry.answerId)));
  const [said, copy] = useCopy();
  const price = Math.abs(entry.amount);
  // The price per million answers: bidding it again is enough to get the same answer.
  const bid = Math.round(price * 1_000_000);
  const request: AskRequest = { questions: [{ id: "q1", text: entry.what, maxCostPerAnswer: bid }] };
  return <>
    <h2>{entry.what}</h2>
    <div class="cs-panel-meta"><StatusBadge tone={refunded ? "neutral" : "success"}>{refunded ? "Refunded" : "Answer"}</StatusBadge><span class="mono cs-muted">{shortHub(entry.hub)}</span><span class="cs-muted">· {when(entry.at)}</span></div>
    <dl class="cs-panel-facts">
      <div><dt>{refunded ? "Refunded" : "Paid"}</dt><dd>{usd(price)}</dd></div>
      <div><dt>From</dt><dd class="mono">{entry.hub}</dd></div>
    </dl>
    <p class="cs-panel-note">{showing ? "The answer is still open in the Playground." : "The Console keeps the newest answer only. Open the question in the Playground and send it again to see the answer; that's a new request."}</p>
    <div class="cs-row-actions">
      {showing
        ? <Button size="sm" variant="solid" href={href("playground")}>See the answer</Button>
        : <Button size="sm" onClick={() => startFromText(entry.what, bid)}>Open in the Playground</Button>}
      <Button size="sm" onClick={() => copy(curlFor(apiBase, request), () => markSeen(account, "copied"))}>{said ?? "Copy as API call"}</Button>
    </div>
    <span class="ui-sr-only" aria-live="polite">{said ?? ""}</span>
  </>;
}
